"use client";

import { useState, useCallback, useEffect, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { CPAOfferCard } from "@/components/cpa-offer-card";
import { CPA_OFFERS, getEligibleOffers } from "@/lib/cpa-offers";

interface CPAOffersCarouselProps {
  onOfferClick?: (offerId: string, link: string) => void;
  excludeOfferId?: string | null;
}

export function CPAOffersCarousel({ onOfferClick, excludeOfferId }: CPAOffersCarouselProps) {
  const offers = useMemo(
    () => getEligibleOffers(CPA_OFFERS, excludeOfferId ?? null),
    [excludeOfferId]
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [held, setHeld] = useState(false);

  useEffect(() => {
    setCurrentIndex(0);
  }, [offers]);

  const safeIndex = offers.length > 0 ? Math.min(currentIndex, offers.length - 1) : 0;
  const currentOffer = offers[safeIndex];

  useEffect(() => {
    if (offers.length <= 1 || held) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const timer = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prev) => (prev + 1) % offers.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [offers.length, held, safeIndex]);

  const goToPrevious = useCallback(() => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + offers.length) % offers.length);
  }, [offers.length]);

  const goToNext = useCallback(() => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % offers.length);
  }, [offers.length]);

  const handleClick = useCallback(() => {
    const offer = offers[safeIndex];
    onOfferClick?.(offer.id, offer.link);
  }, [safeIndex, onOfferClick, offers]);

  if (offers.length === 0) return null;

  return (
    <div
      className="relative"
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocusCapture={() => setHeld(true)}
      onBlurCapture={() => setHeld(false)}
    >
      <div key={currentOffer.id} className="animate-reveal-in" data-direction={direction}>
          <CPAOfferCard
            offer={currentOffer}
            onClick={handleClick}
            ctaLabel="Claim"
            mobileCTALabel="Claim Offer"
            showInfoIcon
            showPromoCode
            className="sm:p-4"
          />
      </div>

      {offers.length > 1 && (
        <div className="mt-3 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              goToPrevious();
            }}
            className="flex size-6 items-center justify-center rounded-full border border-border/50 bg-card text-muted-foreground transition-colors hover:border-border hover:text-foreground"
            aria-label="Previous offer"
          >
            <ChevronLeft className="size-3.5" />
          </button>

          <div className="flex items-center gap-1.5">
            {offers.map((offer, index) => (
              <button
                type="button"
                key={offer.id}
                onClick={(e) => {
                  e.preventDefault();
                  setDirection(index > safeIndex ? 1 : -1);
                  setCurrentIndex(index);
                }}
                className={`h-1.5 rounded-full transition-all duration-200 ${
                  index === safeIndex
                    ? "w-4"
                    : "w-1.5 bg-muted-foreground/30 hover:bg-muted-foreground/50"
                }`}
                style={
                  index === safeIndex
                    ? { backgroundColor: offers[index].brandColor }
                    : undefined
                }
                aria-label={`Go to ${offer.name} offer`}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              goToNext();
            }}
            className="flex size-6 items-center justify-center rounded-full border border-border/50 bg-card text-muted-foreground transition-colors hover:border-border hover:text-foreground"
            aria-label="Next offer"
          >
            <ChevronRight className="size-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
